<template>
    <div class="pm-modal" role="dialog" :aria-label="title">
        <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showCancelButton="showInfoPopupCancelButton">
            <p>{{ infoMessage }}</p>
        </InfoPopup>
        <header class="pm-header">
            <div>
                <span class="pm-eyebrow">Stock library</span>
                <h3>{{ title }}</h3>
            </div>
            <span class="pm-count">{{ filtered.length }} voices</span>
        </header>

        <!-- <input class="pm-search" type="text" v-model="testText" placeholder="Test text for play" /> -->
        <label class="pm-search-wrap">
            <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
            <input class="pm-search" type="text" v-model="q" :placeholder="placeholder" autofocus />
        </label>

        <div v-if="activeFilters.length" class="pm-active-filters">
            <span v-for="filter in activeFilters" :key="filter.key" class="pm-filter-tag">
                {{ filter.label }}
                <button class="pm-filter-tag-x" @click="removeFilter(filter.key)" aria-label="Remove filter">×</button>
            </span>
        </div>
        <div class="pm-controls-row">
            <div class="pm-filters">
                <DropdownList v-model="filters.age" label="Age" :items="optionsAge" />
                <DropdownList v-model="filters.gender" label="Gender" :items="optionsGender" />
                <DropdownList v-model="filters.accent" label="Accent" :items="optionsAccent" />
                <DropdownList v-model="filters.quality" label="Voice Quality" :items="optionsQualityDisplay" />
            </div>
            <div class="pm-gallery-toggle">
                <label class="switch">
                    <input type="checkbox" v-model="onlyMyGallery" />
                    <span class="slider"></span>
                    <span class="switch-label">
                        Only My Gallery
                    </span>
                </label>
            </div>
        </div>
        <div class="pm-list">
            <div v-for="item in filtered" :key="itemKey(item)" class="pm-item vc-card"
                :class="{ recommended: isRecommended(item), playing: isPlaying(item) }" role="button" tabindex="0"
                @click="play(item)" @keydown.enter="play(item)">
                <div class="vc-avatar" :class="{ disabled: isLoading(item) }">
                    <img v-if="voiceImageUrls[item.voice_name]" :src="voiceImageUrls[item.voice_name]"
                        :alt="displayName(item) + ' voice profile'" />
                    <button class="vc-play-btn" type="button" @click.stop="play(item)"
                        :aria-label="isPlaying(item) ? 'Pause voice preview' : 'Play voice preview'">
                        <i v-if="isPlaying(item)" class="fa-solid fa-pause" aria-hidden="true"></i>
                        <i v-else class="fa-solid fa-play" aria-hidden="true"></i>
                    </button>
                    <span v-if="isLoading(item)" class="spinner"></span>
                </div>
                <div class="vc-content">
                    <div class="vc-name-desc">
                        <div class="vc-name-tags">
                            <div class="vc-title-row">
                                <h3>{{ displayName(item) }}</h3>
                                <span v-if="isRecommended(item)" class="vc-recommended-badge">
                                    <i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
                                    AI match
                                </span>
                            </div>
                            <div class="vc-tags">
                                <span v-if="item.accent">{{ item.accent }}</span>
                                <span v-if="item.gender"> · {{ item.gender }}</span>
                                <span v-if="ageLabel(item)"> · {{ ageLabel(item) }}</span>
                            </div>
                        </div>
                        <p class="vc-description">{{ descText(item) }}</p>
                    </div>
                    <div class="vc-middle">
                        <span class="vc-preview-label">{{ isPlaying(item) ? 'Playing preview' : 'Preview voice' }}</span>
                        <div class="vc-progress-bar">
                            <div class="vc-progress-bar-fill"
                                :style="{ width: audioProgress[itemKey(item)] ? (audioProgress[itemKey(item)] * 100) + '%' : '0%' }">
                            </div>
                        </div>
                    </div>
                    <div class="vc-btn-group">
                        <button class="vc-fav-btn" type="button" :class="{ active: isInGallery(item) }"
                            @click.stop="toggleMyGallery(item)"
                            :aria-label="isInGallery(item) ? 'Remove from My Gallery' : 'Add to My Gallery'">
                            <i :class="isInGallery(item) ? 'fa-solid fa-heart' : 'fa-regular fa-heart'"
                                aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="filtered.length === 0" class="pm-empty">
                <i class="fa-regular fa-circle-question" aria-hidden="true"></i>
                <strong>No voices match your filters.</strong>
                <span>Try removing a filter or searching for a broader style.</span>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, computed, reactive, ref, watch, onMounted } from 'vue';
import { postNewTTSRequest, patchRecommendVoicePlayed } from '@/api/services';
import { checkTTSPayload } from '@/utils/TTSPayloadChecker'
import { downloadDemoFile, getVoiceProfilePicUrl } from '@/api/files';
import { patchCollectVoice, deleteCollectVoice, getCollectVoiceIds } from '@/api/myGallery'
import InfoPopup from '@/components/common/InfoPopup.vue';
import DropdownList from '@/components/common/DropdownList.vue';
import { formatVoiceDisplayName } from '@/utils/voiceDisplayName';

export default defineComponent({
    name: 'VGVoiceExplorerModal',
    components: {
        InfoPopup,
        DropdownList,
    },
    props: {
        items: { type: Array, default: () => [] },
        title: { type: String, default: 'Select' },
        placeholder: { type: String, default: 'Search…' },
        labelKey: { type: String, default: 'label' },
        searchKeys: { type: Array, default: () => [] },
        searchText: { type: String, default: '' },
        recommendationIds: { type: Array, default: () => [] },
    },
    emits: ['select', 'update:searchText'],
    setup(props, { emit }) {
        const showInfoPopup = ref(false);
        const infoTitle = ref('');
        const infoMessage = ref('');
        const showInfoPopupCancelButton = ref(true);

        const q = computed({
            get: () => props.searchText,
            set: (val) => emit('update:searchText', val)
        });
        const voiceImageUrls = reactive({});
        const testText = ref('');
        const onlyMyGallery = ref(false);
        const myGalleryIds = ref([]);

        const currentAudio = ref(null);
        const currentPlayingKey = ref('');
        const audioProgress = reactive({});

        const loadingVoices = ref(new Set());
        const KOKORO_MODELS = ['Kokoro v1.0', 'Kokoro_v1.0', 'Kokoro v1_0', 'Kokoro_v1'];
        const searchFields = computed(() =>
            props.searchKeys.length ? props.searchKeys : [props.labelKey]
        );
        const filters = reactive({ age: '', gender: '', accent: '', quality: '' });

        const AGE_BUCKETS = [
            { label: 'Young adult', min: 0, max: 30 },
            { label: 'Middle-aged', min: 31, max: 60 },
            { label: 'Older adult', min: 61, max: Infinity }
        ];
        const optionsAge = computed(() => AGE_BUCKETS.map(b => b.label));
        const optionsGender = computed(() => {
            const vals = props.items.map(i => i.gender).filter(Boolean);
            return Array.from(new Set(vals));
        });
        const optionsAccent = computed(() => {
            const vals = props.items.map(i => i.accent).filter(Boolean);
            return Array.from(new Set(vals));
        });
        const optionsQuality = computed(() => {
            const set = new Set();
            props.items.forEach(i => {
                if (Array.isArray(i.voice_quality)) i.voice_quality.forEach(q => set.add(q));
            });
            return Array.from(set).sort();
        });
        const optionsQualityDisplay = computed(() =>
            optionsQuality.value.map(q => ({
                label: q.charAt(0).toUpperCase() + q.slice(1),
                value: q
            }))
        );
        const filtered = computed(() => {
            const raw = q.value || '';
            // Split by "||" and trim; ignore empty pieces
            const terms = raw
                .split('||')
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);

            let base = props.items;

            // If toggled, restrict to My Gallery voices
            if (onlyMyGallery.value) {
                base = base.filter(it => {
                    const id = it.voice_id ?? it.id ?? it.voiceId ?? '';
                    return myGalleryIds.value.map(String).includes(String(id));
                });
            }

            // Text search (OR across terms, OR across fields)
            if (terms.length) {
                base = base.filter(it =>
                    terms.some(term =>
                        searchFields.value.some(k => {
                            const v = (it?.[k] ?? '').toString().toLowerCase();
                            return v.includes(term);
                        })
                    )
                );
            }

            // Age filter
            if (filters.age) {
                const bucket = AGE_BUCKETS.find(b => b.label === filters.age);
                if (bucket) {
                    base = base.filter(it => {
                        const a = Number(it.age);
                        if (Number.isNaN(a)) return false;
                        return a >= bucket.min && a <= bucket.max;
                    });
                }
            }

            // Gender filter
            if (filters.gender) base = base.filter(it => it.gender === filters.gender);

            // Accent filter
            if (filters.accent) base = base.filter(it => it.accent === filters.accent);

            // Voice quality filter
            if (filters.quality)
                base = base.filter(
                    it => Array.isArray(it.voice_quality) && it.voice_quality.includes(filters.quality)
                );

            return base;
        });

        const itemKey = it => it.id ?? it[props.labelKey] ?? JSON.stringify(it);

        // Active filters for tag display
        const activeFilters = computed(() => {
            const tags = [];
            if (filters.age) tags.push({ key: 'age', label: `Age: ${filters.age}` });
            if (filters.gender) tags.push({ key: 'gender', label: `Gender: ${filters.gender}` });
            if (filters.accent) tags.push({ key: 'accent', label: `Accent: ${filters.accent}` });
            if (filters.quality) {
                // Find display label for quality
                const found = optionsQualityDisplay.value.find(q => q.value === filters.quality);
                tags.push({ key: 'quality', label: `Voice Quality: ${found ? found.label : filters.quality}` });
            }
            return tags;
        });

        function removeFilter(key) {
            filters[key] = '';
        }
        async function loadVoiceImages() {
            props.items.forEach(item => {
                const name = item.voice_name ?? '';
                if (!name) return;
                const imageUrl = getVoiceProfilePicUrl(item.model, name);
                if (imageUrl) voiceImageUrls[name] = imageUrl;
            });
        }
        watch(
            () => props.items,
            () => {
                // clear any previous URLs
                Object.keys(voiceImageUrls).forEach(key => {
                    delete voiceImageUrls[key];
                });
                loadVoiceImages();
            },
            { immediate: true }
        );

        function descText(item) {
            if (item.description && item.description.trim() !== '') return item.description;
            const q = item?.voice_quality;
            if (Array.isArray(q) && q.length) {
                const sentence = `The ${q.slice(0, 9).join(', ')} voice${q.length > 9 ? '…' : ''}.`;
                return sentence.replace(' ,', ',');
            }
            return '';
        }

        function displayName(item) {
            const raw = item.voice_name ?? item[props.labelKey] ?? item.name ?? item.id ?? '';
            const modelName = item.model ?? item.model_name ?? '';
            return formatVoiceDisplayName(raw, modelName);
        }

        function ageLabel(item) {
            const a = Number(item.age);
            if (Number.isNaN(a)) return '';
            const bucket = AGE_BUCKETS.find(b => a >= b.min && a <= b.max);
            return bucket ? bucket.label : '';
        }

        function select(item) {
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
                currentPlayingKey.value = '';
            }
            emit('select', item);
        }

        async function play(item) {
            const modelName = item.model ?? item.model_name ?? '';
            const key = voiceKey(item);

            // If the same item is clicked
            if (currentAudio.value && currentAudio.value._itemKey === key) {
                if (!currentAudio.value.paused) {
                    currentAudio.value.pause();
                    currentPlayingKey.value = '';
                } else {
                    currentAudio.value.play();
                    currentPlayingKey.value = key;
                }
                return;
            }

            // If a different item is clicked, stop current and play new
            if (currentAudio.value) {
                currentAudio.value.pause();
                currentAudio.value = null;
                currentPlayingKey.value = '';
            }

            select(item);

            if (KOKORO_MODELS.includes(modelName)) {
                loadingVoices.value.add(key);
                if (!testText.value || !testText.value.trim()) {
                    try {
                        const data = await downloadDemoFile('Kokoro v1.0', item.voice_name);
                        const wavBlob = new Blob([data], { type: 'audio/wav' });
                        const audioUrl = URL.createObjectURL(wavBlob);

                        const audio = new Audio(audioUrl);
                        audio._itemKey = key; // Track which item this audio belongs to
                        currentAudio.value = audio;
                        currentPlayingKey.value = key;

                        // Track progress
                        audioProgress[itemKey(item)] = 0;
                        audio.ontimeupdate = () => {
                            audioProgress[itemKey(item)] = audio.currentTime / audio.duration;
                        };
                        audio.onended = () => {
                            audioProgress[itemKey(item)] = 0;
                            currentPlayingKey.value = '';
                            URL.revokeObjectURL(audioUrl);
                        };

                        audio.play();

                        const vid = item.voice_id ?? item.id ?? item.voiceId ?? '';
                        const isRecommended = Array.isArray(props.recommendationIds) && props.recommendationIds.includes(vid);
                        if (isRecommended) {
                            patchRecommendVoicePlayed(vid);
                        }
                    } catch (err) {
                        infoTitle.value = 'Error!';
                        infoMessage.value = "Download demo voice failed: " + err;
                        showInfoPopup.value = true;
                        showInfoPopupCancelButton.value = false;
                    }
                    loadingVoices.value.delete(key);
                } else {
                    const payload = {
                        "model": 'Kokoro v1.0',
                        "text": testText.value,
                        'voice_weights': {
                            [item.voice_name]: 1
                        },
                        "speed": 1.0,
                    }
                    try {
                        const payloadCheckingResult = checkTTSPayload(payload);
                        if (!payloadCheckingResult.status) {
                            infoTitle.value = 'Error!';
                            infoMessage.value = "TTS payload check failed " + payloadCheckingResult.returnMessage;
                            showInfoPopup.value = true;
                            showInfoPopupCancelButton.value = false;
                            return;
                        }
                        const response = await postNewTTSRequest(payload);
                        const contentType = response.headers.get('Content-Type');
                        if (contentType.includes('audio')) {

                            const wavBlob = await response.blob();
                            const audioUrl = URL.createObjectURL(wavBlob);

                            const audio = new Audio(audioUrl);
                            audio._itemKey = key;
                            currentAudio.value = audio;
                            currentPlayingKey.value = key;

                            // Track progress
                            audioProgress[itemKey(item)] = 0;
                            audio.ontimeupdate = () => {
                                audioProgress[itemKey(item)] = audio.currentTime / audio.duration;
                            };
                            audio.onended = () => {
                                audioProgress[itemKey(item)] = 0;
                                currentPlayingKey.value = '';
                                URL.revokeObjectURL(audioUrl);
                            };

                            audio.play();

                            const vid = item.voice_id ?? item.id ?? item.voiceId ?? '';
                            const isRecommended = Array.isArray(props.recommendationIds) && props.recommendationIds.includes(vid);
                            if (isRecommended) {
                                patchRecommendVoicePlayed(vid);
                            }
                        } else {
                            const text = await response.text();
                            const message = text.replace(/^"|"$/g, '');
                            infoTitle.value = 'Error!';
                            infoMessage.value = "TTS request failed: " + message;
                            showInfoPopup.value = true;
                            showInfoPopupCancelButton.value = false;
                        }
                    } catch (err) {
                        infoTitle.value = 'Error!';
                        infoMessage.value = "TTS request failed: " + err;
                        showInfoPopup.value = true;
                        showInfoPopupCancelButton.value = false;
                    } finally {
                        loadingVoices.value.delete(key);
                    }
                }
            }
        }

        function voiceKey(item) {
            return item.voice_name ?? item[props.labelKey] ?? item.id ?? '';
        }
        function isLoading(item) {
            return loadingVoices.value.has(voiceKey(item));
        }

        function isPlaying(item) {
            return currentPlayingKey.value === voiceKey(item);
        }

        function isRecommended(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            return Array.isArray(props.recommendationIds)
                && props.recommendationIds.map(String).includes(String(id));
        }


        function isInGallery(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            return myGalleryIds.value.map(String).includes(String(id));
        }

        async function toggleMyGallery(item) {
            const id = item.voice_id ?? item.id ?? item.voiceId ?? '';
            if (!id) {
                infoTitle.value = 'Warning!';
                infoMessage.value = "toggleMyGallery: no voice_id on item: " + item;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            const prev = [...myGalleryIds.value];
            const wasIn = myGalleryIds.value.includes(String(id));
            myGalleryIds.value = wasIn
                ? prev.filter(v => v !== id)
                : [...prev, id];
            try {
                if (wasIn) {
                    await deleteCollectVoice(id);
                } else {
                    await patchCollectVoice(id);
                }
            } catch (e) {
                // rollback on failure
                myGalleryIds.value = prev;
                infoTitle.value = 'Error!';
                infoMessage.value = "toggleMyGallery failed: " + e;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            } finally {
                // background sync to ensure consistency
                refreshMyGallery().catch(() => { });
            }
        }

        async function refreshMyGallery() {
            try {
                const ids = await getCollectVoiceIds();
                myGalleryIds.value = Array.isArray(ids) ? ids : [];
            } catch (e) {
                infoTitle.value = 'Error!';
                infoMessage.value = "refreshMyGallery failed: " + e;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            }
        }
        onMounted(async () => {
            try {
                const ids = await getCollectVoiceIds();
                myGalleryIds.value = Array.isArray(ids) ? ids : [];
            } catch (e) {
                infoTitle.value = 'Error!';
                infoMessage.value = "Failed to load collected voice IDs: " + e;
                showInfoPopup.value = true;
                showInfoPopupCancelButton.value = false;
            }
        });
        return {
            KOKORO_MODELS,
            q,
            testText,
            onlyMyGallery,
            myGalleryIds,
            filters,
            optionsAge,
            optionsGender,
            optionsAccent,
            optionsQuality,
            optionsQualityDisplay,
            filtered,
            searchFields,
            itemKey,
            descText,
            displayName,
            ageLabel,
            play,
            select,
            toggleMyGallery,
            isInGallery,
            voiceKey,
            loadingVoices,
            isLoading,
            isPlaying,
            isRecommended,
            voiceImageUrls,
            showInfoPopup,
            infoTitle,
            infoMessage,
            showInfoPopupCancelButton,
            currentAudio,
            currentPlayingKey,
            audioProgress,
            activeFilters,
            removeFilter,
        };
    }
});
</script>

<style scoped>
.pm-modal {
    background: #fff;
    width: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100%;
}

.pm-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0rem 1rem;
}

.pm-search {
    font-family: var(--app-font);
    margin: .75rem 1rem;
    padding: .5rem .75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    width: calc(100% - 2rem);
}

.pm-controls-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.pm-filters {
    display: flex;
    flex-direction: row;
    gap: 5rem;
    padding: 1rem 1rem 0.75rem 1rem;
    box-sizing: border-box;
}

.pm-filter {
    appearance: none;
    width: 100%;
    padding: 10px 14px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 0.95rem;
    color: #1a2b4d;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E") no-repeat right 12px center/12px 8px;
}

.pm-filter:focus {
    outline: none;
    border-color: var(--accent-indigo);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, .15);
}

.pm-list {
    flex: 1 1 auto;
    overflow-y: auto;
    display: grid;
    grid-auto-rows: 120px;
    /* This sets the row height */
    align-content: start;
}

.pm-item {
    padding: .6rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    cursor: pointer;
    transition: background .15s;
    overflow: hidden;
}

.pm-empty {
    padding: 1rem;
    text-align: center;
    color: #888;
}

.pm-active-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 1rem 0.5rem 1rem;
    align-items: center;
    min-height: 32px;
}

.pm-filter-tag {
    background: #f3eaff;
    color: var(--purple);
    border-radius: 16px;
    padding: 0.25rem 0.75rem 0.25rem 0.75rem;
    font-size: 0.92rem;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}

.pm-filter-tag-x {
    background: none;
    border: none;
    color: var(--purple);
    font-size: 1.1em;
    margin-left: 0.2em;
    cursor: pointer;
    padding: 0 0.2em;
    line-height: 1;
    border-radius: 50%;
    transition: background 0.15s;
}

.pm-filter-tag-x:hover {
    background: var(--purple-ultra-light);
    color: #6c3fc5;
}

.vc-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;
}

.vc-name-tags h3 {
    font-size: 1.1rem;
}

.vc-card {
    transition: all 0.3s ease;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: .3rem;
    padding: 0.6rem 0.6rem 0.6rem 1.2rem;
    height: 100%;
    overflow: hidden;
}

.vc-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    border: white;
}

.vc-tags {
    font-size: 0.8rem;
    color: var(--purple);
    font-weight: 500;
}

.vc-description {
    font-size: 0.95rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    line-height: 1.3;
}

.vc-fav-btn {
    border: none;
    background: #f9f4ff;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    line-height: 32px;
    text-align: center;
    cursor: pointer;
    font-size: 16px;
    color: #ff4a6a;
    flex-shrink: 0;
    transition: background .15s;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: none;
}

.vc-fav-btn:hover {
    background: #ffdfe5;
}

.vc-fav-btn.active {
    background: #f9f4ff;
}

.vc-fav-btn svg {
    display: block;
}


.pm-gallery-toggle {
    padding: 0 1rem 0.75rem 1rem;
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
}

.pm-gallery-toggle .switch {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    gap: 0.5rem;
    user-select: none;
    font-size: 0.9rem;
    color: #1a2b4d;
}

.pm-gallery-toggle .switch input {
    display: none;
}

.pm-gallery-toggle .switch .slider {
    position: relative;
    width: 44px;
    height: 24px;
    background: #d1d5db;
    border-radius: 12px;
    transition: background .2s;
    flex-shrink: 0;
}

.pm-gallery-toggle .switch .slider::after {
    content: "";
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform .2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
}

.pm-gallery-toggle .switch input:checked+.slider {
    background: var(--purple-hover);
}

.pm-gallery-toggle .switch input:checked+.slider::after {
    transform: translateX(20px);
}

.pm-gallery-toggle .switch-label {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-weight: 500;
    color: #4a4a6a;
}


@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.vc-avatar {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    margin-right: 12px;
    overflow: hidden;
    flex-shrink: 0;
    background: #f0f0f0;
    position: relative;
    cursor: pointer;
    transition: transform 0.2s, opacity 0.2s;
}

.vc-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.2s;
}

.vc-avatar.disabled {
    pointer-events: none;
    opacity: 0.6;
}

.vc-avatar:hover:not(.disabled) img {
    transform: scale(1.05);
}

.vc-avatar .spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-width: 4px;
    border-style: solid;
    border-color: #4a61ff transparent transparent transparent;
    animation: spin 0.6s linear infinite;
    border-radius: 50%;
}

.vc-content {
    display: grid;
    grid-template-columns: 50% 40% 10%;
    gap: 1.5rem;
    width: 100%;
    height: 100%;
    align-items: stretch;
}

.vc-name-desc {
    grid-column: 1;
    flex: unset;
    min-width: 0;
}

.vc-name-tags {
    text-align: left;
}

.vc-middle {
    display: flex;
    flex-direction: column;
    grid-column: 2;
    width: 100%;
    min-width: 0;
    margin: 0;
    align-items: center;
    justify-content: center;
}

.vc-btn-group {
    display: flex;
    grid-column: 3;
    width: 100%;
    min-width: 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    justify-self: end;
}

.vc-progress-bar {
    width: 100%;
    height: 6px;
    background: #eee;
    border-radius: 3px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
}

.vc-progress-bar-fill {
    height: 100%;
    background: var(--purple, #8800ff);
    border-radius: 3px;
    position: absolute;
    left: 0;
    top: 0;
    transition: width 0.1s linear;
    width: 0%;
}

.pm-modal {
    background: transparent;
    gap: 0.85rem;
    padding: 0 1.25rem 1.25rem;
}

.pm-header {
    padding: 0.25rem 0 0;
}

.pm-eyebrow {
    color: var(--purple);
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
}

.pm-header h3 {
    color: #0f172a;
    font-size: 1.15rem;
}

.pm-count {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 999px;
    color: #475569;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.4rem 0.7rem;
}

.pm-search-wrap {
    align-items: center;
    background: #ffffff;
    border: 1px solid #dbe3ee;
    border-radius: 14px;
    display: flex;
    gap: 0.65rem;
    margin: 0;
    padding: 0 0.9rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;
}

.pm-search-wrap:focus-within {
    border-color: rgba(136, 0, 255, 0.45);
    box-shadow: 0 0 0 3px rgba(136, 0, 255, 0.08);
}

.pm-search-wrap i {
    color: #94a3b8;
}

.pm-search {
    border: 0;
    border-radius: 0;
    color: #0f172a;
    margin: 0;
    padding: 0.85rem 0;
    width: 100%;
}

.pm-search:focus {
    outline: none;
}

.pm-controls-row {
    align-items: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    gap: 1rem;
    padding: 0.75rem;
}

.pm-filters {
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0;
}

.pm-gallery-toggle {
    padding: 0;
}

.pm-active-filters {
    min-height: 0;
    padding: 0;
}

.pm-filter-tag {
    background: #faf5ff;
    border: 1px solid rgba(136, 0, 255, 0.16);
    border-radius: 999px;
}

.pm-list {
    display: grid;
    gap: 0.85rem;
    grid-auto-rows: minmax(132px, auto);
    overflow: visible;
}

.pm-item {
    padding: 0;
}

.vc-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.04);
    min-height: 132px;
    padding: 1rem;
}

.vc-card:hover {
    border-color: #dbe3ee;
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.08);
    transform: translateY(-2px);
}

.vc-card:focus {
    outline: none;
}

.vc-card:focus-visible {
    border-color: #dbe3ee;
    box-shadow: 0 0 0 3px rgba(136, 0, 255, 0.14), 0 18px 34px rgba(15, 23, 42, 0.08);
    outline: none;
}

.vc-card.recommended {
    border-color: rgba(136, 0, 255, 0.4);
    box-shadow: 0 16px 36px rgba(136, 0, 255, 0.11);
}

.vc-card.playing {
    background: linear-gradient(135deg, #ffffff, #fbf7ff);
}

.vc-avatar {
    background: #eef2ff;
    border-radius: 18px;
    height: 74px;
    margin-right: 1rem;
    width: 74px;
}

.vc-play-btn {
    align-items: center;
    background: rgba(15, 23, 42, 0.68);
    border: 0;
    border-radius: 999px;
    bottom: 0.45rem;
    color: #ffffff;
    cursor: pointer;
    display: flex;
    height: 32px;
    justify-content: center;
    position: absolute;
    right: 0.45rem;
    transition: background 0.2s ease, transform 0.2s ease;
    width: 32px;
}

.vc-play-btn:hover,
.vc-play-btn:focus {
    background: var(--purple);
    outline: none;
    transform: scale(1.04);
}

.vc-content {
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(0, 1.4fr) minmax(160px, 0.8fr) auto;
}

.vc-title-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    justify-content: flex-start;
    text-align: left;
}

.vc-name-tags h3 {
    color: #0f172a;
    font-size: 1.1rem;
    line-height: 1.2;
    text-align: left;
}

.vc-tags {
    color: #6b21a8;
    font-size: 0.82rem;
    margin-top: 0.3rem;
}

.vc-recommended-badge {
    align-items: center;
    background: #faf5ff;
    border: 1px solid rgba(136, 0, 255, 0.18);
    border-radius: 999px;
    color: var(--purple);
    display: inline-flex;
    font-size: 0.75rem;
    font-weight: 700;
    gap: 0.35rem;
    padding: 0.25rem 0.55rem;
}

.vc-description {
    color: #475569;
    display: -webkit-box;
    line-height: 1.35;
    margin: 0.45rem 0 0;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.vc-preview-label {
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 700;
    margin-bottom: 0.45rem;
    text-transform: uppercase;
}

.vc-progress-bar {
    background: #e2e8f0;
    height: 8px;
}

.vc-progress-bar-fill {
    background: linear-gradient(90deg, var(--purple), #3b82f6);
}

.vc-fav-btn {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: #94a3b8;
}

.vc-fav-btn:hover,
.vc-fav-btn.active {
    background: #fff1f2;
    border-color: #fecdd3;
    color: #e11d48;
}

.pm-empty {
    align-items: center;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 16px;
    color: #64748b;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    grid-column: 1 / -1;
    padding: 2rem;
}

.pm-empty i {
    color: var(--purple);
    font-size: 1.4rem;
}

.pm-empty strong {
    color: #0f172a;
}

@media (max-width: 760px) {
    .pm-modal {
        padding: 0 0.85rem 0.85rem;
    }

    .pm-header,
    .pm-controls-row,
    .vc-card,
    .vc-content {
        align-items: stretch;
        flex-direction: column;
    }

    .pm-controls-row,
    .vc-card {
        display: flex;
    }

    .vc-content {
        display: flex;
        gap: 0.85rem;
    }

    .vc-btn-group {
        align-items: flex-start;
    }
}
</style>
